import { IntlShape } from "react-intl";
import { markdownToPlainText } from "../../../utils/markdown";

type PlaygroundAiStatus = {
  website?: boolean;
  text?: boolean;
  files?: boolean;
  provider?: boolean;
  wa?: boolean;
  ig?: boolean;
  fm?: boolean;
};

type PromptValues = {
  company?: string;
  channels?: string;
  topic?: string;
};

type PromptConfig = {
  key: string;
  id: string;
  values?: PromptValues;
};

export type StarterPrompt = {
  key: string;
  text: string;
};

export const MAX_STARTER_PROMPTS = 3;

const STOP_WORDS = new Set([
  "about",
  "after",
  "also",
  "and",
  "are",
  "but",
  "can",
  "como",
  "con",
  "customer",
  "customers",
  "de",
  "del",
  "desde",
  "does",
  "for",
  "from",
  "how",
  "ideal",
  "las",
  "los",
  "mais",
  "más",
  "para",
  "por",
  "que",
  "qué",
  "servicio",
  "servicios",
  "service",
  "services",
  "sobre",
  "the",
  "this",
  "una",
  "with",
  "your",
]);

const getCompanyLabel = (companyName: string | undefined, intl: IntlShape) => {
  if (typeof companyName === "string" && companyName.trim().length > 0) {
    return companyName.trim();
  }
  return intl.formatMessage({ id: "ai.playground.starters.companyFallback" });
};

const getConnectedChannelLabels = (
  status: PlaygroundAiStatus | undefined,
  intl: IntlShape,
) => {
  const channels: string[] = [];
  if (status?.wa) {
    channels.push(
      intl.formatMessage({ id: "ai.playground.starters.channel.whatsapp" }),
    );
  }
  if (status?.ig) {
    channels.push(
      intl.formatMessage({ id: "ai.playground.starters.channel.instagram" }),
    );
  }
  if (status?.fm) {
    channels.push(
      intl.formatMessage({ id: "ai.playground.starters.channel.messenger" }),
    );
  }
  return channels;
};

const normalizeTopic = (topic: string) =>
  topic
    .replace(/[“”"']/g, "")
    .replace(/\s+/g, " ")
    .trim();

const isValidTopic = (topic: string, company: string) => {
  if (!topic || topic.length < 3 || topic.length > 48) {
    return false;
  }
  const normalized = topic.toLowerCase();
  const companyNormalized = company.toLowerCase();
  if (
    companyNormalized &&
    (normalized === companyNormalized || normalized.includes(companyNormalized))
  ) {
    return false;
  }
  if (normalized.includes("@")) {
    return false;
  }
  const words = normalized.split(/\s+/g);
  if (words.length === 0) {
    return false;
  }
  if (words.every((w) => STOP_WORDS.has(w) || w.length < 3)) {
    return false;
  }
  return true;
};

const extractQuotedTopics = (text: string, company: string) => {
  const matches = text.match(/["“”'‘’]([^"“”'‘’]{3,60})["“”'‘’]/g) || [];
  return matches
    .map((raw) => normalizeTopic(raw))
    .filter((topic) => isValidTopic(topic, company));
};

const extractBigramTopics = (text: string, company: string) => {
  const words = (text.match(/[A-Za-zÀ-ÿ0-9][A-Za-zÀ-ÿ0-9&+.-]*/g) || [])
    .map((w) => w.trim())
    .filter(Boolean);

  const filtered = words.filter((word) => {
    const lower = word.toLowerCase();
    return word.length >= 3 && !STOP_WORDS.has(lower);
  });

  const counts = new Map<string, number>();
  for (let i = 0; i < filtered.length - 1; i += 1) {
    const a = filtered[i];
    const b = filtered[i + 1];
    const topic = normalizeTopic(`${a} ${b}`);
    if (!isValidTopic(topic, company)) continue;
    counts.set(topic, (counts.get(topic) || 0) + 1);
  }

  return Array.from(counts.entries())
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return b[0].length - a[0].length;
    })
    .map(([topic]) => topic);
};

const getContextTopics = ({
  contextText,
  company,
}: {
  contextText?: string;
  company: string;
}) => {
  const plain = markdownToPlainText(contextText || "")
    .replace(/\s+/g, " ")
    .trim();
  if (plain.length < 30) {
    return [];
  }

  const combined = [...extractQuotedTopics(plain, company), ...extractBigramTopics(plain, company)];
  const deduped: string[] = [];
  for (const topic of combined) {
    if (!deduped.some((item) => item.toLowerCase() === topic.toLowerCase())) {
      deduped.push(topic);
    }
    if (deduped.length >= MAX_STARTER_PROMPTS) {
      break;
    }
  }
  return deduped;
};

export const getPlaygroundStarterPrompts = ({
  intl,
  companyName,
  status,
  contextText,
}: {
  intl: IntlShape;
  companyName?: string;
  status?: PlaygroundAiStatus;
  contextText?: string;
}): StarterPrompt[] => {
  const company = getCompanyLabel(companyName, intl);
  const contextTopics = getContextTopics({ contextText, company });
  const topicA = contextTopics[0];
  const topicB = contextTopics[1] || contextTopics[0];

  if (topicA) {
    const contextPrompts: PromptConfig[] = [
      {
        key: "context_what_is_company",
        id: "ai.playground.starters.prompt.context.whatIs",
        values: { topic: company },
      },
      {
        key: "context_tell_me_more",
        id: "ai.playground.starters.prompt.context.tellMeMore",
        values: { topic: topicA },
      },
      {
        key: "context_how_works",
        id: "ai.playground.starters.prompt.context.howWorks",
        values: { topic: topicB },
      },
    ];

    return contextPrompts.map((prompt) => ({
      key: prompt.key,
      text: intl.formatMessage({ id: prompt.id }, prompt.values),
    }));
  }

  const connectedChannels = getConnectedChannelLabels(status, intl);
  const hasSources = Boolean(status?.website || status?.text || status?.files);
  const hasProvider = Boolean(status?.provider);
  const hasChannels = connectedChannels.length > 0;

  const prompts: PromptConfig[] = [
    {
      key: "company",
      id: "ai.playground.starters.prompt.company",
      values: { company },
    },
    hasSources
      ? {
          key: "knowledge_ready",
          id: "ai.playground.starters.prompt.knowledge.ready",
          values: { company },
        }
      : {
          key: "knowledge_missing",
          id: "ai.playground.starters.prompt.knowledge.missing",
          values: { company },
        },
    hasProvider
      ? {
          key: "provider_ready",
          id: "ai.playground.starters.prompt.provider.ready",
        }
      : {
          key: "provider_missing",
          id: "ai.playground.starters.prompt.provider.missing",
        },
    hasChannels
      ? {
          key: "channels_ready",
          id: "ai.playground.starters.prompt.channels.ready",
          values: { channels: connectedChannels.join(", ") },
        }
      : {
          key: "channels_missing",
          id: "ai.playground.starters.prompt.channels.missing",
        },
  ];

  return prompts.slice(0, MAX_STARTER_PROMPTS).map((prompt) => ({
    key: prompt.key,
    text: intl.formatMessage({ id: prompt.id }, prompt.values),
  }));
};
